import { useTranslation } from 'react-i18next'
import { CircleDot, Loader2, Users } from 'lucide-react'
import type { LookupResponse, OrderType } from '@/api/model'
import { InstanceStatus } from '@/api/model/instanceStatus'
import { Button } from '@/components/shadcn/button'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterSearch } from '@/components/common/filters/FilterSearch'
import { FilterTopText } from '@/components/common/filters/FilterTopText'
import { FilterDatePicker } from '@/components/common/filters/FilterDatePicker'
import { FilterDropdown } from '@/components/common/filters/FilterDropdown'
import { IncentivationExportButton } from './IncentivationExportButton'

/** The three statuses the active-instance rule can yield — the obsolete enum members are not offered. */
const FILTERABLE_STATUSES = [InstanceStatus.Open, InstanceStatus.Completed, InstanceStatus.Closed] as const

export interface IncentivationFilterValues {
  start: string
  end: string
  search: string
  groupIds: string[]
  instanceStatuses: InstanceStatus[]
}

interface Props {
  moduleId: number
  values: IncentivationFilterValues
  /** Called on every change — there is no apply button, each change refetches. */
  onChange: (patch: Partial<IncentivationFilterValues>) => void
  /** Groups the module is assigned to — the only ones worth filtering by. */
  groupOptions: LookupResponse[]
  /** Current table sort — passed through to the export so the file is ordered like the table. */
  sort?: string
  order?: OrderType
  /** Whether the edit toggle is offered at all (hidden for modules with no editable score). */
  canEdit: boolean
  editMode: boolean
  isSaving: boolean
  onEnterEdit: () => void
  onSave: () => void
  onCancel: () => void
}

/**
 * Filter row for the Incentivierung tab: start/end date + group + status + search. Fully
 * controlled — every change is lifted immediately and refetches (search is debounced inside
 * `FilterSearch`), like the employees list. On the right sit the export button and the edit
 * toggle: "Edit" in read mode, "Save" + "Cancel" in edit mode.
 */
export function IncentivationFilters({
  moduleId, values, onChange, groupOptions, sort, order, canEdit, editMode, isSaving, onEnterEdit, onSave, onCancel,
}: Props) {
  const { t } = useTranslation(['action-modules-detail', 'incentivation', 'filters'])

  // Same labels as the status column's tooltip, so filter and table read alike
  const statusOptions: LookupResponse[] = FILTERABLE_STATUSES.map((status) => ({
    id: status,
    name: t(`incentivation:subtype.status.${status.toLowerCase()}`),
  }))

  return (
    <FilterRow>
      <FilterSearch
        searchLabel={t('incentivation.filters.searchPlaceholder')}
        search={values.search}
        onChange={(search) => onChange({ search: search ?? '' })}
      />
      <FilterTopText text={t('filters:datetime.from')}>
        <FilterDatePicker value={values.start} onChange={(value) => onChange({ start: value })} />
      </FilterTopText>

      <FilterTopText text={t('filters:datetime.to')}>
        <FilterDatePicker value={values.end} onChange={(value) => onChange({ end: value })} />
      </FilterTopText>

      <FilterDropdown
        icon={Users}
        label={t('filters:dropdown.groups')}
        availableOptions={groupOptions}
        selectedOptions={values.groupIds}
        onSelect={(groupIds) => onChange({ groupIds })}
        onReset={() => onChange({ groupIds: [] })}
      />
      <FilterDropdown
        icon={CircleDot}
        label={t('filters:dropdown.status')}
        availableOptions={statusOptions}
        selectedOptions={values.instanceStatuses}
        onSelect={(statuses) => onChange({ instanceStatuses: statuses as InstanceStatus[] })}
        onReset={() => onChange({ instanceStatuses: [] })}
      />
      <div className="ml-auto flex items-end gap-2">
        <IncentivationExportButton moduleId={moduleId} values={values} sort={sort} order={order} />
        {canEdit && (editMode ? (
          <>
            <Button size="sm" variant="outline" className="h-9" onClick={onCancel}>
              {t('incentivation.edit.cancel')}
            </Button>
            <Button size="sm" className="h-9" onClick={onSave} disabled={isSaving}>
              {isSaving && <Loader2 className="size-4 animate-spin" />}
              {t('incentivation.edit.save')}
            </Button>
          </>
        ) : (
          <Button size="sm" variant="outline" className="h-9" onClick={onEnterEdit}>
            {t('incentivation.edit.edit')}
          </Button>
        ))}
      </div>
    </FilterRow>
  )
}
